import {
  alertEventSchema,
  autoUnlockStatusSchema,
  checklistSchema,
  deployOverviewSchema,
  environmentSchema,
  releaseSchema,
  maintenanceReportSchema,
  fileVersionsSchema,
  reachabilityReportSchema,
  backupJobSchema,
  backupRunSchema,
  certificateListSchema,
  commandOutcomeSchema,
  deployRunSchema,
  projectSchema,
  overviewSchema,
  rebootOutcomeSchema,
  securityOverviewSchema,
  updatePreviewSchema,
  updateRunResultSchema,
  serviceListSchema,
  alertRuleSchema,
  apiErrorSchema,
  apiRoutes,
  channelStatusSchema,
  channelTestResultSchema,
  bookmarkSchema,
  directoryListingSchema,
  fileActionResultSchema,
  factsResponseSchema,
  metricsSeriesSchema,
  monitorStatusSchema,
  fileContentSchema,
  transferItemSchema,
  workloadActionResultSchema,
  workloadListSchema,
  transferListSchema,
  writeFileResultSchema,
  connectionStateSchema,
  connectionTestResultSchema,
  healthResponseSchema,
  serverSchema,
  snippetRunResponseSchema,
  snippetSchema,
  terminalSessionSchema,
  vaultStatusSchema,
  type AlertEvent,
  type AutoUnlockStatus,
  type Checklist,
  type CreateEnvironmentInput,
  type DeployOverview,
  type Environment,
  type Release,
  type MaintenanceReport,
  type FileVersions,
  type ReachabilityReport,
  type BackupJob,
  type BackupRun,
  type CertificateList,
  type CommandOutcome,
  type CreateBackupJobInput,
  type CreateProjectInput,
  type DeployRun,
  type Project,
  type Overview,
  type RebootOutcome,
  type SecurityOverview,
  type UpdatePreview,
  type UpdateRunResult,
  type UpdateScope,
  type ServiceStatus,
  type UpdateProjectInput,
  type AlertRule,
  type Bookmark,
  type ChannelConfig,
  type ChannelStatus,
  type CreateAlertRuleInput,
  type ChmodInput,
  type ChownInput,
  type ConflictPolicy,
  type ConnectionState,
  type CreateDirectoryInput,
  type DeletePathsInput,
  type DirectoryListing,
  type EnqueueTransferInput,
  type FileActionResult,
  type FactsResponse,
  type FileContent,
  type FileSide,
  type MetricRange,
  type MetricsSeries,
  type MonitorStatus,
  type RenameInput,
  type TransferItem,
  type WriteFileInput,
  type UpdateAlertRuleInput,
  type WorkloadActionInput,
  type WorkloadActionResult,
  type WorkloadSection,
  type WriteFileResult,
  type ConnectionTestResult,
  type CreateServerInput,
  type HealthResponse,
  type CreateSnippetInput,
  type CreateTerminalSessionInput,
  type RunSnippetInput,
  type Server,
  type Snippet,
  type TerminalSession,
  type UpdateServerInput,
  type UpdateSnippetInput,
  type VaultStatus,
  configImportResultSchema,
  remoteInstanceSchema,
  totpSetupSchema,
  totpStatusSchema,
  type ConfigImportResult,
  type RemoteInstance,
  type TotpSetup,
  type TotpStatus,
} from '@kontor/shared'
import { z, type ZodType } from 'zod'

/**
 * Where the API lives.
 *
 * Empty string means "same origin", which is the case in every deployment we have:
 *   - development: the Vite dev server proxies /api to the backend on :3000
 *   - packaged desktop: Electron loads the page from the in-process backend itself
 *   - self-hosted: nginx serves the SPA and the API under one host
 *
 * VITE_KONTOR_API_BASE exists for the Phase 7 case of a desktop client pointed at a remote
 * backend (PROMPT.md §3.1).
 */
const API_BASE = import.meta.env.VITE_KONTOR_API_BASE ?? ''

/** An API call that failed in a way the user should be told about, in German. */
export class ApiRequestError extends Error {
  readonly code: string
  readonly status: number | undefined
  readonly issues: { path: string; message: string }[]

  constructor(
    message: string,
    options: {
      code: string
      status?: number
      cause?: unknown
      issues?: { path: string; message: string }[]
    },
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause })
    this.name = 'ApiRequestError'
    this.code = options.code
    this.status = options.status
    this.issues = options.issues ?? []
  }

  /** True when the backend wants the vault unlocked before it will answer. */
  get isLocked(): boolean {
    return this.code === 'vault_locked'
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
}

async function request<T>(
  path: string,
  schema: ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET'

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        Accept: 'application/json',
        ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      credentials: 'same-origin',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    })
  } catch (cause) {
    throw new ApiRequestError('Das Backend ist nicht erreichbar. Läuft der Kontor-Server?', {
      code: 'network_unreachable',
      cause,
    })
  }

  const rawBody = await response.text()

  if (!response.ok) {
    const parsedError = apiErrorSchema.safeParse(safeJsonParse(rawBody))
    if (parsedError.success) {
      throw new ApiRequestError(parsedError.data.error.message, {
        code: parsedError.data.error.code,
        status: response.status,
        issues: parsedError.data.error.issues ?? [],
      })
    }
    throw new ApiRequestError(`Das Backend hat mit HTTP ${String(response.status)} geantwortet.`, {
      code: 'unexpected_status',
      status: response.status,
    })
  }

  const parsed = schema.safeParse(rawBody === '' ? null : safeJsonParse(rawBody))
  if (!parsed.success) {
    throw new ApiRequestError(
      `Die Antwort von ${path} passt nicht zum erwarteten Format. ` +
        'Vermutlich laufen Frontend und Backend in unterschiedlichen Versionen.',
      { code: 'schema_mismatch', status: response.status, cause: parsed.error },
    )
  }
  return parsed.data
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    // Returning the raw text lets the schema check produce the better error message; a bare
    // SyntaxError here would say nothing about which endpoint misbehaved.
    return raw
  }
}

export function fetchHealth(): Promise<HealthResponse> {
  return request(apiRoutes.health, healthResponseSchema)
}

export function fetchVaultStatus(): Promise<VaultStatus> {
  return request(apiRoutes.vaultStatus, vaultStatusSchema)
}

export function setupVault(masterPassword: string): Promise<VaultStatus> {
  return request(apiRoutes.vaultSetup, vaultStatusSchema, {
    method: 'POST',
    body: { masterPassword },
  })
}

export function unlockVault(masterPassword: string, totpCode?: string): Promise<VaultStatus> {
  return request(apiRoutes.vaultUnlock, vaultStatusSchema, {
    method: 'POST',
    body: { masterPassword, ...(totpCode === undefined || totpCode === '' ? {} : { totpCode }) },
  })
}

export function fetchTotpStatus(): Promise<TotpStatus> {
  return request(apiRoutes.totpStatus, totpStatusSchema)
}

export function startTotpSetup(): Promise<TotpSetup> {
  return request(apiRoutes.totpSetup, totpSetupSchema, { method: 'POST' })
}

export function confirmTotpSetup(input: {
  secret: string
  recoveryCodes: string[]
  code: string
}): Promise<TotpStatus> {
  return request(apiRoutes.totpConfirm, totpStatusSchema, { method: 'POST', body: input })
}

export function disableTotp(masterPassword: string): Promise<TotpStatus> {
  return request(apiRoutes.totpDisable, totpStatusSchema, {
    method: 'POST',
    body: { masterPassword },
  })
}

export function fetchFileVersions(serverId: string, path: string): Promise<FileVersions> {
  const query = new URLSearchParams({ serverId, path })
  return request(`${apiRoutes.fileVersions}?${query.toString()}`, fileVersionsSchema)
}

export function fetchMaintenanceReport(
  group: string,
  from: string,
  to: string,
): Promise<MaintenanceReport> {
  const query = new URLSearchParams({ group, from, to })
  return request(`${apiRoutes.maintenanceReport}?${query.toString()}`, maintenanceReportSchema)
}

const deployLockSchema = z
  .object({
    holder: z.string(),
    kind: z.string(),
    acquiredAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
  })
  .nullable()

export type DeployLock = z.infer<typeof deployLockSchema>

const releaseStepSchema = z.object({
  seq: z.number().int(),
  name: z.string(),
  status: z.enum(['pending', 'running', 'ok', 'failed', 'skipped']),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  exitCode: z.number().int().nullable(),
  durationMs: z.number().int().nullable(),
  logExcerpt: z.string().nullable(),
})

const releaseWithStepsSchema = releaseSchema.extend({ steps: z.array(releaseStepSchema) })

export type ReleaseWithSteps = z.infer<typeof releaseWithStepsSchema>

export function fetchReleases(projectId: string): Promise<ReleaseWithSteps[]> {
  return request(apiRoutes.projectReleases(projectId), z.array(releaseWithStepsSchema))
}

export function buildRelease(projectId: string, commitSha: string): Promise<Release> {
  return request(apiRoutes.projectReleases(projectId), releaseSchema, {
    method: 'POST',
    body: { commitSha },
  })
}

export function fetchDeployLock(projectId: string): Promise<DeployLock> {
  return request(apiRoutes.projectLock(projectId), deployLockSchema)
}

export function releaseDeployLock(projectId: string): Promise<{ ok: boolean }> {
  return request(apiRoutes.projectLock(projectId), z.object({ ok: z.boolean() }), {
    method: 'DELETE',
  })
}

export function updatePreview(projectId: string): Promise<DeployRun> {
  return request(apiRoutes.previewUpdate(projectId), deployRunSchema, { method: 'POST' })
}

export function refreshPreviewData(projectId: string): Promise<DeployRun> {
  return request(apiRoutes.previewRefreshData(projectId), deployRunSchema, { method: 'POST' })
}

export function fetchDeployOverview(projectId: string): Promise<DeployOverview> {
  return request(apiRoutes.projectDeployOverview(projectId), deployOverviewSchema)
}

export function createEnvironment(
  projectId: string,
  body: CreateEnvironmentInput,
): Promise<Environment> {
  return request(apiRoutes.projectEnvironments(projectId), environmentSchema, {
    method: 'POST',
    body,
  })
}

export function updateEnvironment(
  projectId: string,
  environmentId: string,
  body: Record<string, unknown>,
): Promise<Environment> {
  return request(apiRoutes.projectEnvironment(projectId, environmentId), environmentSchema, {
    method: 'PATCH',
    body,
  })
}

export function fetchChecklist(serverId: string): Promise<Checklist> {
  return request(apiRoutes.serverChecklist(serverId), checklistSchema)
}

export function checkReachability(host: string, serverId?: string): Promise<ReachabilityReport> {
  const query = new URLSearchParams({ host })
  if (serverId !== undefined && serverId !== '') query.set('serverId', serverId)
  return request(`${apiRoutes.reachability}?${query.toString()}`, reachabilityReportSchema)
}

export function fetchAutoUnlock(): Promise<AutoUnlockStatus> {
  return request(apiRoutes.vaultAutoUnlock, autoUnlockStatusSchema)
}

export function setAutoUnlock(enabled: boolean): Promise<AutoUnlockStatus> {
  return request(apiRoutes.vaultAutoUnlock, autoUnlockStatusSchema, {
    method: 'POST',
    body: { enabled },
  })
}

export function fetchRemoteInstance(): Promise<RemoteInstance> {
  return request(apiRoutes.remoteInstance, remoteInstanceSchema)
}

export function setRemoteInstance(url: string | null): Promise<RemoteInstance> {
  return request(apiRoutes.remoteInstance, remoteInstanceSchema, { method: 'PUT', body: { url } })
}

/**
 * Downloads the configuration archive. It goes through fetch rather than a plain link so a
 * failure shows up as a German message instead of a browser error page.
 */
export async function downloadConfigExport(): Promise<{ blob: Blob; filename: string }> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}${apiRoutes.configExport}`, { credentials: 'same-origin' })
  } catch (cause) {
    throw new ApiRequestError('Das Backend ist nicht erreichbar. Läuft der Kontor-Server?', {
      code: 'network_unreachable',
      cause,
    })
  }

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(safeJsonParse(await response.text()))
    throw new ApiRequestError(
      parsed.success ? parsed.data.error.message : 'Der Export ließ sich nicht erzeugen.',
      { code: parsed.success ? parsed.data.error.code : 'export_failed', status: response.status },
    )
  }

  const disposition = response.headers.get('content-disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(disposition)
  return { blob: await response.blob(), filename: match?.[1] ?? 'kontor-konfiguration.zip' }
}

export async function importConfig(file: File): Promise<ConfigImportResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}${apiRoutes.configImport}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/zip' },
      body: file,
    })
  } catch (cause) {
    throw new ApiRequestError('Das Backend ist nicht erreichbar. Läuft der Kontor-Server?', {
      code: 'network_unreachable',
      cause,
    })
  }

  const raw = await response.text()
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(safeJsonParse(raw))
    throw new ApiRequestError(
      parsed.success ? parsed.data.error.message : 'Der Import ist fehlgeschlagen.',
      { code: parsed.success ? parsed.data.error.code : 'import_failed', status: response.status },
    )
  }

  return configImportResultSchema.parse(safeJsonParse(raw))
}

export function lockVault(): Promise<VaultStatus> {
  return request(apiRoutes.vaultLock, vaultStatusSchema, { method: 'POST' })
}

export function fetchOverview(): Promise<Overview> {
  return request(apiRoutes.overview, overviewSchema)
}

export function fetchServers(): Promise<Server[]> {
  return request(apiRoutes.servers, z.array(serverSchema))
}

export function createServer(input: CreateServerInput): Promise<Server> {
  return request(apiRoutes.servers, serverSchema, { method: 'POST', body: input })
}

export function updateServer(id: string, patch: UpdateServerInput): Promise<Server> {
  return request(apiRoutes.server(id), serverSchema, { method: 'PATCH', body: patch })
}

export function deleteServer(id: string): Promise<null> {
  return request(apiRoutes.server(id), z.null(), { method: 'DELETE' })
}

export function testServerConnection(id: string): Promise<ConnectionTestResult> {
  return request(apiRoutes.serverTestConnection(id), connectionTestResultSchema, {
    method: 'POST',
  })
}

export function trustHostKey(id: string, fingerprint: string): Promise<Server> {
  return request(apiRoutes.serverTrustHostKey(id), serverSchema, {
    method: 'POST',
    body: { fingerprint },
  })
}

export function connectServer(id: string): Promise<ConnectionState> {
  return request(apiRoutes.serverConnect(id), connectionStateSchema, { method: 'POST' })
}

export function disconnectServer(id: string): Promise<ConnectionState> {
  return request(apiRoutes.serverDisconnect(id), connectionStateSchema, { method: 'POST' })
}

export function fetchConnections(): Promise<ConnectionState[]> {
  return request(apiRoutes.connections, z.array(connectionStateSchema))
}

export function fetchSnippets(): Promise<Snippet[]> {
  return request(apiRoutes.snippets, z.array(snippetSchema))
}

export function createSnippet(input: CreateSnippetInput): Promise<Snippet> {
  return request(apiRoutes.snippets, snippetSchema, { method: 'POST', body: input })
}

export function updateSnippet(id: string, patch: UpdateSnippetInput): Promise<Snippet> {
  return request(apiRoutes.snippet(id), snippetSchema, { method: 'PATCH', body: patch })
}

export function deleteSnippet(id: string): Promise<null> {
  return request(apiRoutes.snippet(id), z.null(), { method: 'DELETE' })
}

export function runSnippet(id: string, input: RunSnippetInput) {
  return request(apiRoutes.snippetRun(id), snippetRunResponseSchema, {
    method: 'POST',
    body: input,
  })
}

export function fetchTerminalSessions(): Promise<TerminalSession[]> {
  return request(apiRoutes.terminalSessions, z.array(terminalSessionSchema))
}

export function createTerminalSession(input: CreateTerminalSessionInput): Promise<TerminalSession> {
  return request(apiRoutes.terminalSessions, terminalSessionSchema, {
    method: 'POST',
    body: input,
  })
}

export function closeTerminalSession(id: string): Promise<null> {
  return request(apiRoutes.terminalSession(id), z.null(), { method: 'DELETE' })
}

/** Absolute websocket URL for a path under the API, derived from wherever the API lives. */
function socketUrl(path: string): string {
  const base = API_BASE === '' ? window.location.origin : API_BASE
  const url = new URL(path, base)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}

export function eventsSocketUrl(): string {
  return socketUrl(apiRoutes.events)
}

export function terminalSocketUrl(sessionId: string): string {
  return socketUrl(apiRoutes.terminalSocket(sessionId))
}

/** Builds `?a=b` from the parts that are actually set. */
function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, value)
  }
  const text = search.toString()
  return text === '' ? '' : `?${text}`
}

export interface DirectoryRequest {
  side: FileSide
  path: string
  serverId?: string | undefined
}

export function fetchDirectory(input: DirectoryRequest): Promise<DirectoryListing> {
  return request(
    `${apiRoutes.filesDirectory}${query({
      side: input.side,
      path: input.path,
      serverId: input.serverId,
    })}`,
    directoryListingSchema,
  )
}

export function createDirectory(body: CreateDirectoryInput): Promise<FileActionResult> {
  return request(apiRoutes.filesMkdir, fileActionResultSchema, { method: 'POST', body })
}

export function renamePath(body: RenameInput): Promise<FileActionResult> {
  return request(apiRoutes.filesRename, fileActionResultSchema, { method: 'POST', body })
}

export function deletePaths(body: DeletePathsInput): Promise<FileActionResult> {
  return request(apiRoutes.filesDelete, fileActionResultSchema, { method: 'POST', body })
}

export function chmodPath(body: ChmodInput): Promise<FileActionResult> {
  return request(apiRoutes.filesChmod, fileActionResultSchema, { method: 'POST', body })
}

export function chownPath(body: ChownInput): Promise<FileActionResult> {
  return request(apiRoutes.filesChown, fileActionResultSchema, { method: 'POST', body })
}

export function fetchFileContent(
  serverId: string,
  path: string,
  sudoPassword?: string,
): Promise<FileContent> {
  return request(
    `${apiRoutes.filesContent}${query({ serverId, path, sudoPassword })}`,
    fileContentSchema,
  )
}

export function writeFileContent(body: WriteFileInput): Promise<WriteFileResult> {
  return request(apiRoutes.filesContent, writeFileResultSchema, { method: 'PUT', body })
}

export function fetchBookmarks(serverId: string): Promise<Bookmark[]> {
  return request(apiRoutes.serverBookmarks(serverId), z.array(bookmarkSchema))
}

export function createBookmark(
  serverId: string,
  body: { label: string; remotePath: string },
): Promise<Bookmark> {
  return request(apiRoutes.serverBookmarks(serverId), bookmarkSchema, { method: 'POST', body })
}

export function deleteBookmark(serverId: string, id: string): Promise<null> {
  return request(apiRoutes.serverBookmark(serverId, id), z.null(), { method: 'DELETE' })
}

export function fetchTransfers(): Promise<TransferItem[]> {
  return request(apiRoutes.transfers, transferListSchema).then((result) => result.items)
}

export function enqueueTransfer(body: EnqueueTransferInput): Promise<TransferItem[]> {
  return request(apiRoutes.transfers, z.object({ items: z.array(transferItemSchema) }), {
    method: 'POST',
    body,
  }).then((result) => result.items)
}

export function cancelTransfer(id: string): Promise<null> {
  return request(apiRoutes.transferCancel(id), z.null(), { method: 'POST' })
}

export function retryTransfer(id: string): Promise<null> {
  return request(apiRoutes.transferRetry(id), z.null(), { method: 'POST' })
}

export function resolveTransferConflict(
  id: string,
  body: { policy: ConflictPolicy; applyToAll: boolean },
): Promise<null> {
  return request(apiRoutes.transferResolve(id), z.null(), { method: 'POST', body })
}

export function clearFinishedTransfers(): Promise<null> {
  return request(apiRoutes.transfers, z.null(), { method: 'DELETE' })
}

export function fetchFacts(serverId: string, refresh = false): Promise<FactsResponse> {
  return request(
    `${apiRoutes.serverFacts(serverId)}${refresh ? '?refresh=true' : ''}`,
    factsResponseSchema,
  )
}

export function fetchWorkloads(serverId: string): Promise<WorkloadSection[]> {
  return request(apiRoutes.serverWorkloads(serverId), workloadListSchema).then(
    (result) => result.sections,
  )
}

export function runWorkloadAction(
  serverId: string,
  body: WorkloadActionInput,
): Promise<WorkloadActionResult> {
  return request(apiRoutes.serverWorkloadAction(serverId), workloadActionResultSchema, {
    method: 'POST',
    body,
  })
}

export function logSocketUrl(
  serverId: string,
  params: { kind: string; id: string; tail: number },
): string {
  const url = new URL(socketUrl(apiRoutes.serverLogSocket(serverId)))
  url.searchParams.set('kind', params.kind)
  url.searchParams.set('id', params.id)
  url.searchParams.set('tail', String(params.tail))
  return url.toString()
}

export function fetchMonitorStatus(): Promise<MonitorStatus> {
  return request(apiRoutes.monitoringStatus, monitorStatusSchema)
}

export function fetchMetrics(serverId: string, range: MetricRange): Promise<MetricsSeries> {
  return request(`${apiRoutes.serverMetrics(serverId)}?range=${range}`, metricsSeriesSchema)
}

export function fetchAlertRules(): Promise<AlertRule[]> {
  return request(apiRoutes.alertRules, z.array(alertRuleSchema))
}

export function createAlertRule(body: CreateAlertRuleInput): Promise<AlertRule> {
  return request(apiRoutes.alertRules, alertRuleSchema, { method: 'POST', body })
}

export function updateAlertRule(id: string, body: UpdateAlertRuleInput): Promise<AlertRule> {
  return request(apiRoutes.alertRule(id), alertRuleSchema, { method: 'PATCH', body })
}

export function deleteAlertRule(id: string): Promise<null> {
  return request(apiRoutes.alertRule(id), z.null(), { method: 'DELETE' })
}

export function fetchAlertEvents(limit = 50): Promise<AlertEvent[]> {
  return request(`${apiRoutes.alertEvents}?limit=${String(limit)}`, z.array(alertEventSchema))
}

export function fetchAlertChannels(): Promise<ChannelStatus[]> {
  return request(apiRoutes.alertChannels, z.array(channelStatusSchema))
}

export function saveAlertChannel(body: ChannelConfig): Promise<ChannelStatus> {
  return request(apiRoutes.alertChannel(body.channel), channelStatusSchema, {
    method: 'PUT',
    body,
  })
}

export function deleteAlertChannel(channel: string): Promise<null> {
  return request(apiRoutes.alertChannel(channel), z.null(), { method: 'DELETE' })
}

export function testAlertChannel(channel: string): Promise<{ ok: boolean; message: string }> {
  return request(apiRoutes.alertChannelTest(channel), channelTestResultSchema, { method: 'POST' })
}

export function fetchProjects(serverId?: string): Promise<Project[]> {
  const suffix = serverId === undefined ? '' : `?serverId=${encodeURIComponent(serverId)}`
  return request(`${apiRoutes.projects}${suffix}`, z.array(projectSchema))
}

export function createProject(body: CreateProjectInput): Promise<Project> {
  return request(apiRoutes.projects, projectSchema, { method: 'POST', body })
}

export function updateProject(id: string, body: UpdateProjectInput): Promise<Project> {
  return request(apiRoutes.project(id), projectSchema, { method: 'PATCH', body })
}

export function deleteProject(id: string): Promise<null> {
  return request(apiRoutes.project(id), z.null(), { method: 'DELETE' })
}

export function fetchDeployRun(projectId: string): Promise<DeployRun | null> {
  return request(apiRoutes.projectDeploy(projectId), deployRunSchema.nullable())
}

export function startDeploy(projectId: string, sudoPassword?: string): Promise<DeployRun> {
  return request(apiRoutes.projectDeploy(projectId), deployRunSchema, {
    method: 'POST',
    body: sudoPassword === undefined ? {} : { sudoPassword },
  })
}

export function cancelDeploy(projectId: string): Promise<null> {
  return request(apiRoutes.projectDeployCancel(projectId), z.null(), { method: 'POST' })
}

export function deploySocketUrl(projectId: string): string {
  return socketUrl(apiRoutes.projectDeploySocket(projectId))
}

export function fetchCertificates(serverId: string): Promise<CertificateList> {
  return request(apiRoutes.serverCertificates(serverId), certificateListSchema)
}

export function runCertbotAction(
  serverId: string,
  action: 'dry-run' | 'renew',
): Promise<CommandOutcome> {
  return request(apiRoutes.serverCertbot(serverId), commandOutcomeSchema, {
    method: 'POST',
    body: { action },
  })
}

export function fetchServices(serverId: string): Promise<ServiceStatus[]> {
  return request(apiRoutes.serverServices(serverId), serviceListSchema).then(
    (result) => result.services,
  )
}

export function reloadNginxConfig(serverId: string): Promise<CommandOutcome> {
  return request(apiRoutes.serverNginxReload(serverId), commandOutcomeSchema, {
    method: 'POST',
    body: {},
  })
}

export function fetchSecurity(serverId: string): Promise<SecurityOverview> {
  return request(apiRoutes.serverSecurity(serverId), securityOverviewSchema)
}

export function fetchUpdates(serverId: string): Promise<UpdatePreview> {
  return request(apiRoutes.serverUpdates(serverId), updatePreviewSchema)
}

export function rebootServer(serverId: string, sudoPassword?: string): Promise<RebootOutcome> {
  return request(apiRoutes.serverReboot(serverId), rebootOutcomeSchema, {
    method: 'POST',
    body: sudoPassword === undefined ? {} : { sudoPassword },
  })
}

export function runUpdates(
  serverId: string,
  scope: UpdateScope,
  sudoPassword?: string,
): Promise<UpdateRunResult> {
  return request(apiRoutes.serverUpdatesRun(serverId), updateRunResultSchema, {
    method: 'POST',
    body: { scope, ...(sudoPassword === undefined ? {} : { sudoPassword }) },
  })
}

export function fetchBackupJobs(serverId?: string): Promise<BackupJob[]> {
  const suffix = serverId === undefined ? '' : `?serverId=${encodeURIComponent(serverId)}`
  return request(`${apiRoutes.backupJobs}${suffix}`, z.array(backupJobSchema))
}

export function createBackupJob(body: CreateBackupJobInput): Promise<BackupJob> {
  return request(apiRoutes.backupJobs, backupJobSchema, { method: 'POST', body })
}

export function updateBackupJob(
  id: string,
  body: { enabled?: boolean; keep?: number; scheduleMin?: number | null },
): Promise<BackupJob> {
  return request(apiRoutes.backupJob(id), backupJobSchema, { method: 'PATCH', body })
}

export function deleteBackupJob(id: string): Promise<null> {
  return request(apiRoutes.backupJob(id), z.null(), { method: 'DELETE' })
}

export function runBackupJob(id: string): Promise<BackupRun> {
  return request(apiRoutes.backupJobRun(id), backupRunSchema, { method: 'POST' })
}

export function fetchBackupRuns(id: string, limit = 20): Promise<BackupRun[]> {
  return request(`${apiRoutes.backupJobRuns(id)}?limit=${String(limit)}`, z.array(backupRunSchema))
}
